import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ViewListIcon } from "@heroicons/react/outline";
import { FileText, Globe, MapPin, Quote } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ContainerFileAnnotation,
  FileAnnotation,
  MapAnnotation,
  WebAnnotation,
} from "@/types/prompt-blueprint";
import { markdownComponents } from "@/components/VibeChat/markdownComponents";

function CitedTextBlock({ text }: { text: string }) {
  return (
    <div className="mt-2 border-l-4 border-gray-300 bg-gray-100/60 py-1.5 pl-3 pr-2">
      <div className="prose prose-sm max-w-none text-sm text-gray-700 prose-p:my-0.5 prose-ol:my-0.5 prose-ul:my-0.5 prose-li:my-0">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {text}
        </ReactMarkdown>
      </div>
    </div>
  );
}

interface CitationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileCitations: FileAnnotation[];
  webCitations: WebAnnotation[];
  mapCitations: MapAnnotation[];
  containerFileCitations?: ContainerFileAnnotation[];
}

export const CitationsModal = ({
  isOpen,
  onClose,
  fileCitations,
  webCitations,
  mapCitations,
  containerFileCitations = [],
}: CitationsModalProps) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="flex max-h-[80vh] max-w-2xl flex-col">
      <DialogHeader className="flex-shrink-0">
        <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
          <ViewListIcon className="h-6 w-6" />
          Sources
        </DialogTitle>
      </DialogHeader>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-2">
        {fileCitations.length > 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <Quote className="h-5 w-5 text-gray-600" />
              <h3 className="text-md font-medium text-gray-900">Cited</h3>
            </div>

            <div className="space-y-3">
              {fileCitations.map(citation => (
                <div
                  key={citation.file_id}
                  className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100"
                >
                  <FileText className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-gray-900">{citation.filename}</div>
                    <div className="mt-1 truncate text-sm text-gray-500">
                      File ID: {citation.file_id}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {webCitations.length > 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-gray-600" />
              <h3 className="text-md font-medium text-gray-900">Sites</h3>
            </div>

            <div className="space-y-3">
              {webCitations.map(citation => (
                <div
                  key={citation.url}
                  className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100"
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className="overflow-hidden font-medium text-gray-900"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {citation.title}
                    </div>
                    <a
                      href={citation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 block truncate text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {citation.url}
                    </a>
                    {citation.cited_text && <CitedTextBlock text={citation.cited_text} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {mapCitations.length > 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-gray-600" />
              <h3 className="text-md font-medium text-gray-900">Places</h3>
            </div>

            <div className="space-y-3">
              {mapCitations.map(citation => (
                <div
                  key={citation.url || citation.title}
                  className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100"
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className="overflow-hidden font-medium text-gray-900"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {citation.title}
                    </div>
                    {citation.url && (
                      <a
                        href={citation.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 block truncate text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {citation.url}
                      </a>
                    )}
                    {citation.cited_text && <CitedTextBlock text={citation.cited_text} />}
                    {citation.place_id && (
                      <div className="mt-1 truncate text-sm text-gray-500">
                        Place ID: {citation.place_id}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {containerFileCitations.length > 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-600" />
              <h3 className="text-md font-medium text-gray-900">Container Files</h3>
            </div>

            <div className="space-y-3">
              {containerFileCitations.map(citation => (
                <div
                  key={citation.file_id || citation.container_id}
                  className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-gray-900">
                      {citation.filename || "File"}
                    </div>
                    <div className="mt-1 truncate text-sm text-gray-500">
                      Container: {citation.container_id}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {fileCitations.length === 0 &&
          webCitations.length === 0 &&
          mapCitations.length === 0 &&
          containerFileCitations.length === 0 && (
            <div className="py-8 text-center text-gray-500">
              <ViewListIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <p>No sources found</p>
            </div>
          )}
      </div>
    </DialogContent>
  </Dialog>
);
